import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import Button from '../../components/Button';
import styles from '../../css/admin/Login.module.css';
import { useLanguage } from '../../hooks/LanguageContext';
import useAxiosAdminFetch from '../../hooks/useAxiosAdminFetch';
import Swal from 'sweetalert2';
import translations from './translations';
import { isApp } from '../../utils/isApp';

// [신규] 마켓(naegift-mobile 등)의 "상점관리자" 메뉴 진입점 — storeNo를 모르는 상태에서
//  로그인 → 그 계정에 연결된 storeNo로 스스로 이동한다.
//  기존 /mobile/login/:storeNo(Login.js)는 특정 상점 URL이 이미 정해진 상태 전용이라
//  건드리지 않고 이 컴포넌트를 별도로 추가했다(라우트: /mobile/login, storeNo 파라미터 없음).
export default function AdminLoginGlobal() {
  const { language } = useLanguage();
  const t = translations[language];
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchData } = useAxiosAdminFetch();

  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get('redirect');

  const routeAfterLogin = storeNo => {
    // [APP-FORK] 앱에서만, 화면 이동 전에 FCM 토큰 등록을 띄운다.
    //   admin 은 로그인 후 navigate(SPA 내부 이동)라 문서가 다시 로드되지 않아
    //   appAuthReceiver 의 모듈 평가 시점 등록이 비로그인으로 걸러진 채 끝난다.
    //   ★await 하지 않는다 — 이동을 지연시키지 않고 실패는 무시한다.
    //   ★동적 import — isApp.js:22-23 과 같은 방식이라 웹 번들에 포함되지 않는다.
    if (isApp()) {
      import('../../utils/appAuthReceiver')
        .then(m => m.registerFcmIfNeeded())
        .catch(() => {});
    }
    if (redirectTo) {
      navigate(decodeURIComponent(redirectTo));
    } else {
      navigate(`/mobile/giftstatus/${storeNo}`);
    }
  };

  const handleLogin = async () => {
    const normalizedUserId = String(userId || '').trim();
    if (!normalizedUserId || !password) {
      setShowWarning(true);
      return;
    }
    setLoading(true);
    try {
      const response = await fetchData({
        url: `${process.env.REACT_APP_API_SERVER}login`,
        method: 'post',
        data: { autoLogin: true, adminId: normalizedUserId, password },
      });
      if (response?.data?.resultCode !== 200) {
        // useAxiosAdminFetch.fetchData는 실패해도 reject하지 않고 axios 에러의
        // response 객체를 그대로 반환한다 — 그래서 실제 실패 사유는 response.status(HTTP
        // 상태코드)에 있고, response.data.resultCode는 성공 응답에만 있는 필드라 에러 때는 항상 undefined.
        const code = response?.status;
        const message =
          code === 403
            ? t.storeDeactivated
            : code === 401
              ? t.wrongPassword
              : code === 404
                ? t.userNotFound
                : t.loginFailedGeneric;
        Swal.fire({
          icon: 'error',
          title: t.loginFailed,
          text: message,
          confirmButtonText: t.confirm,
        });
        return;
      }
      // Login.js와 동일 규약 — admin_userid를 저장해둬야 이후 페이지들의 세션 보조 판정이 맞물린다.
      localStorage.setItem('admin_userid', normalizedUserId);
      // [2026-10-30 X-02] 비밀번호는 브라우저에 저장하지 않는다(읽는 곳 없음). 로그인 판정은 admin_userid + 서버 쿠키

      // storeNo를 모르니 detail 조회로 알아낸 뒤, 무조건 그 상점으로 이동(불일치 경고 없음
      // — 애초에 비교 대상 storeNo가 없으므로 Login.js의 mismatch 분기는 여기선 불필요).
      const detail = await fetchData({
        url: `${process.env.REACT_APP_API_SERVER}detail/${encodeURIComponent(normalizedUserId)}`,
        method: 'get',
      });
      const detailStoreNo = detail?.data?.data?.storeNo;
      if (!detailStoreNo) {
        Swal.fire({
          icon: 'error',
          title: t.loginFailed,
          text: t.loginFailedGeneric,
          confirmButtonText: t.confirm,
        });
        return;
      }
      routeAfterLogin(detailStoreNo);
    } catch (error) {
      const code = error?.response?.data?.resultCode || error?.response?.status;
      const message =
        code === 403
          ? t.storeDeactivated
          : code === 401
            ? t.wrongPassword
            : code === 404
              ? t.userNotFound
              : t.loginFailedGeneric;
      Swal.fire({
        icon: 'error',
        title: t.loginFailed,
        text: message,
        confirmButtonText: t.confirm,
      });
    } finally {
      setLoading(false);
    }
  };

  // [APP-FORK] 이미 로그인된 상태로 /mobile/login(상점공통) 재진입 시 폼 대신 자동 리다이렉트.
  //   Login.js:218-257 verifyExistingSession 과 동일 패턴이나, 이 라우트엔 URL storeNo가 없어
  //   일치 비교 없이 detail API가 준 storeNo를 그대로 써서 이동한다(로그인 성공 경로 routeAfterLogin 재사용).
  //   admin_userid는 있는데 detail이 401/에러(useAxiosAdminFetch가 refresh 시도 후에도 실패)면
  //   detailStoreNo 없음 → 리다이렉트 안 함 → 폼 렌더(무한루프 없음).
  useEffect(() => {
    const verifyExistingSession = async () => {
      const adminId = String(localStorage.getItem('admin_userid') || '').trim();
      if (!adminId) return;
      try {
        const detail = await fetchData({
          url: `${process.env.REACT_APP_API_SERVER}detail/${encodeURIComponent(adminId)}`,
          method: 'get',
        });
        const detailStoreNo = detail?.data?.data?.storeNo;
        if (detailStoreNo) {
          routeAfterLogin(detailStoreNo);
        }
      } catch (_) {}
    };
    verifyExistingSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.formContainer}>
        <div className={styles.adminLogin}>
          {language === 'ko' ? '상점관리자 로그인' : 'Store Admin Login'}
        </div>
        <form
          className={styles.formFrame}
          onSubmit={e => {
            e.preventDefault();
            handleLogin();
          }}
        >
          <div className={styles.formFrame}>
            <input
              type="text"
              placeholder={t.loginUsername}
              value={userId}
              className={styles.input}
              onChange={e => setUserId(e.target.value)}
              autoComplete="username"
            />
            <div className={styles.passwordField}>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={t.loginPassword}
                className={styles.input}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
              />
              <button
                type="button"
                className={styles.passwordToggle}
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
              >
                {showPassword ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" /><circle cx="12" cy="12" r="3" /></svg>
                )}
              </button>
            </div>
            <button
              type="submit"
              style={{ display: 'none' }}
              aria-hidden="true"
            />
          </div>
          {showWarning && <div>{t.loginEnterBoth}</div>}
          <Button color="orange" onClick={handleLogin}>
            {loading ? '...' : t.login}
          </Button>
        </form>
      </div>
    </div>
  );
}
